import { useEffect, useRef, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoDocument, IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoProfile, IOpoProfileData, IOpoProfileRequest } from '@nkr/models/opo-profile';
import { downloadFile } from '@nkr/services/download-file';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { BillingActionCreators, IValidateAchRoutingNumberResponse } from '@nkr/store/billing';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoProfileData | null,
    loading: boolean,
    error: string,
    save: (request: IOpoProfileRequest, documents: Record<number, File>) => Promise<IOpoSaveResult>,
    isRoutingNumberValid: (routingNumber: string) => Promise<boolean>,
    downloadDocument: (document: IOpoDocument) => Promise<void>
}

const useOpoProfile = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoProfileData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const routingNumberResults = useRef(new Map<string, boolean>());

    const baseUrl = `${__API__}/opo/${opoId}/profile`;

    const getProfile = (): Promise<ITypedBaseResponse<IOpoProfileData>> => dispatch(AuthActionCreators.apiGet({ url: baseUrl }));

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting OPO profile, one moment please...'));

            try {
                const response = await getProfile();

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load the OPO profile');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load the OPO profile');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId]);

    const save = async (request: IOpoProfileRequest, documents: Record<number, File>): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync('Saving OPO profile, one moment please...'));

        try {
            const formData = new FormData();

            Object.entries(request)
                .filter(([, value]) => value != null)
                .forEach(([key, value]) => formData.append(key, String(value)));

            Object.entries(documents).forEach(([documentTypeId, file]) => formData.append(`document-${documentTypeId}`, file));

            const response: ITypedBaseResponse<{ profile: IOpoProfile, documents: IOpoDocument[] }> = await dispatch(AuthActionCreators.apiPutFormData({ url: baseUrl, data: formData }));

            if (response && response.data.success) {
                setData(current => current ? { ...current, profile: response.data.results.profile, documents: response.data.results.documents } : current);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || 'Unable to save the OPO profile' };
        } catch (err) {
            return { success: false, message: 'Unable to save the OPO profile' };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const isRoutingNumberValid = async (routingNumber: string): Promise<boolean> => {
        if (routingNumberResults.current.has(routingNumber)) {
            return routingNumberResults.current.get(routingNumber);
        }

        try {
            const response: IValidateAchRoutingNumberResponse = await dispatch(BillingActionCreators.validateAchRoutingNumber(routingNumber));
            const isValid = response != null && response.data.success;

            routingNumberResults.current.set(routingNumber, isValid);

            return isValid;
        } catch (err) {
            return false;
        }
    };

    const downloadDocument = async (document: IOpoDocument) => {
        const response: any = await dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/documents/${document.documentTypeId}/download`, responseType: 'blob' }));

        downloadFile(response.data, `${document.name.toLowerCase().replace(/ /g, '-')}-${opoId}.pdf`, 'application/pdf');
    };

    return { data, loading, error, save, isRoutingNumberValid, downloadDocument };
}

export default useOpoProfile;
